import { z } from 'zod';

/** Platform identifier: a UUID string. Platform-generated ids are UUID v7 (`newId()`). */
export const Id = z.uuid().meta({ id: 'Id', description: 'UUID; platform-generated ids are UUID v7' });
export type Id = z.infer<typeof Id>;

/** Tenant identifier: lowercase slug, e.g. `demo`. */
export const TenantId = z
  .string()
  .regex(/^[a-z0-9][a-z0-9_-]{0,62}$/, 'tenant id must be a lowercase slug')
  .meta({ id: 'TenantId' });
export type TenantId = z.infer<typeof TenantId>;

/** ISO-8601 date-time string with `Z` or an offset, e.g. `2026-10-22T10:00:00.000Z`. */
export const IsoDateTime = z.iso.datetime({ offset: true }).meta({ id: 'IsoDateTime' });
export type IsoDateTime = z.infer<typeof IsoDateTime>;

/** Semantic version string, e.g. `1.0.0`. */
export const SemVer = z
  .string()
  .regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/, 'must be a semantic version')
  .meta({ id: 'SemVer' });

/** Idempotency key supplied by a client or derived by the platform (`${taskId}:${actionId}`). */
export const IdempotencyKey = z
  .string()
  .min(1)
  .max(255)
  .regex(/^[\x21-\x7e]+$/, 'idempotency key must be printable ASCII without spaces')
  .meta({ id: 'IdempotencyKey' });

/** Arbitrary JSON object. */
export const JsonObject = z.record(z.string(), z.unknown());
export type JsonObject = z.infer<typeof JsonObject>;

/** A JSON Schema document (draft 2020-12) carried as data. */
export const JsonSchemaObject = z
  .record(z.string(), z.unknown())
  .meta({ id: 'JsonSchemaObject', description: 'JSON Schema (draft 2020-12) document' });
export type JsonSchemaObject = z.infer<typeof JsonSchemaObject>;

/** Reference to a business entity in a system of record. */
export const EntityRef = z
  .object({ type: z.string().min(1), id: z.string().min(1) })
  .meta({ id: 'EntityRef' });
export type EntityRef = z.infer<typeof EntityRef>;

/** SHA-256 hex digest (64 lowercase hex chars). */
export const Sha256Hex = z.string().regex(/^[a-f0-9]{64}$/, 'must be a sha-256 hex digest');

let lastMs = -1;
let sequence = 0;

/**
 * New UUID v7 (RFC 9562): 48-bit Unix ms timestamp, 12-bit monotonic sequence, 62 random bits.
 * Ids generated in one process sort by creation time. Uses Web Crypto, works in Bun and browsers.
 */
export function newId(): string {
  let ms = Date.now();
  if (ms > lastMs) {
    lastMs = ms;
    sequence = crypto.getRandomValues(new Uint16Array(1))[0] ?? 0;
    sequence &= 0x7ff;
  } else {
    sequence += 1;
    if (sequence > 0xfff) {
      lastMs += 1;
      sequence = 0;
    }
    ms = lastMs;
  }
  const ts = ms.toString(16).padStart(12, '0');
  const random = crypto.getRandomValues(new Uint32Array(2));
  const hi = (((random[0] ?? 0) & 0x3fffffff) | 0x80000000) >>> 0;
  const hiHex = hi.toString(16).padStart(8, '0');
  const loHex = ((random[1] ?? 0) >>> 0).toString(16).padStart(8, '0');
  return `${ts.slice(0, 8)}-${ts.slice(8, 12)}-7${sequence.toString(16).padStart(3, '0')}-${hiHex.slice(0, 4)}-${hiHex.slice(4)}${loHex}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Id helpers: `Ids.newId()`, `Ids.isId(value)`, `Ids.timestampOf(uuidv7)`. */
export const Ids = {
  newId,
  isId(value: unknown): value is string {
    return typeof value === 'string' && UUID_RE.test(value);
  },
  /** Creation time encoded in a UUID v7, or null for other versions. */
  timestampOf(id: string): Date | null {
    if (!UUID_RE.test(id) || id[14] !== '7') return null;
    return new Date(Number.parseInt(id.slice(0, 8) + id.slice(9, 13), 16));
  },
} as const;
